import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { IAuthState } from '../types/interfaces/IAuth';
import { config } from '../config/environment';

const ACTIVITY_UPDATE_THROTTLE_MS = 15 * 1000;

const getSessionDurationMs = (): number =>
  config.auth.sessionDurationMinutes * 60 * 1000;

const getSessionAnchor = (state: Pick<IAuthState, 'lastActivityTime' | 'loginTime'>): number | null =>
  state.lastActivityTime ?? state.loginTime;

// Función para verificar si la sesión ha expirado
const isSessionExpired = (sessionAnchor: number): boolean => {
  const now = Date.now();
  return now - sessionAnchor > getSessionDurationMs();
};

export const useAuthStore = create<IAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      loginTime: null,
      lastActivityTime: null,
      isSpecialtyActive: null,
      requiresPasswordChange: false,

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          loginTime: null,
          lastActivityTime: null,
          isSpecialtyActive: null,
          requiresPasswordChange: false,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      // Función para verificar y limpiar sesión expirada
      checkSessionExpiration: () => {
        const state = get();
        const sessionAnchor = getSessionAnchor(state);

        if (sessionAnchor && isSessionExpired(sessionAnchor)) {
          set({
            user: null,
            isAuthenticated: false,
            loginTime: null,
            lastActivityTime: null,
            isSpecialtyActive: null,
          });
          return true; // Sesión expirada
        }
        return false; // Sesión válida
      },

      // Función para obtener tiempo restante de sesión (en minutos)
      getSessionTimeRemaining: (): number => {
        const state = get();
        const sessionAnchor = getSessionAnchor(state);
        if (!sessionAnchor) return 0;

        const now = Date.now();
        const timeRemaining = getSessionDurationMs() - (now - sessionAnchor);

        return Math.max(0, Math.ceil(timeRemaining / (60 * 1000)));
      },

      registerActivity: () => {
        const state = get();
        if (!state.isAuthenticated || !state.loginTime) {
          return;
        }

        const now = Date.now();
        if (state.lastActivityTime && now - state.lastActivityTime < ACTIVITY_UPDATE_THROTTLE_MS) {
          return;
        }

        set({ lastActivityTime: now });
      },

      // Métodos para actualizar el estado desde el hook
      setUser: (user: any, isSpecialtyActive: boolean, requiresPasswordChange = false) => {
        const now = Date.now();
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          loginTime: now,
          lastActivityTime: now,
          isSpecialtyActive,
          requiresPasswordChange,
        });
      },

      patchUser: userPatch => {
        set(state => ({
          user: state.user
            ? {
                ...state.user,
                ...userPatch,
              }
            : state.user,
        }));
      },

      clearRequiresPasswordChange: () => {
        set({ requiresPasswordChange: false });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        loginTime: state.loginTime,
        lastActivityTime: state.lastActivityTime,
        isSpecialtyActive: state.isSpecialtyActive,
        requiresPasswordChange: state.requiresPasswordChange,
      }),
    }
  )
);
